import React, { useContext, useEffect, useState } from 'react';

import { infoTechApi as api } from 'api/infoTech';
import { UserContext } from 'contexts/UserContext';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { MultiSelect } from 'shadcn/components/ui/MultiSelect';
import { Separator } from 'shadcn/components/ui/Separator';
// ShadCN UI components
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'shadcn/components/ui/Tabs';

// Profile roles table (expanded row content)
function ProfileRolesTable({ profileId }) {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchProfileRoles() {
            setIsLoading(true);
            try {
                const response = await api.getProfileRoles(profileId);
                if (response.status === false) {
                    setError(`No roles found: ${response.msg}`);
                    setRoles([]);
                } else {
                    // Sort roles alphabetically by description
                    const sortedRoles = response.data.rows.sort((a, b) => a.description.localeCompare(b.description));
                    setRoles(sortedRoles);
                    setError(null);
                }
            } catch (err) {
                setError('Failed to load roles');
                setRoles([]);
            } finally {
                setIsLoading(false);
            }
        }

        fetchProfileRoles();
    }, [profileId]);

    if (isLoading) {
        return (
            <div className="py-4 text-center">
                <div className="animate-pulse">Loading roles...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-4 text-center">
                <div className="text-red-500">{error}</div>
            </div>
        );
    }

    return (
        <Card className="mb-4 border-t-0 rounded-t-none">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg">Profile Roles</CardTitle>
                <CardDescription>Roles assigned to this profile</CardDescription>
            </CardHeader>
            <CardContent>
                {roles.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">No roles assigned to this profile</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-20">ID</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Description</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {roles.map((role) => (
                                <TableRow key={role.id}>
                                    <TableCell className="font-mono">{role.id}</TableCell>
                                    <TableCell>{role.description}</TableCell>
                                    <TableCell>{role.longDescription}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}

// Main component
function ViewOnlyJalapenoProfiles({ accountType = 'MISFITS MARKET' }) {
    const [profiles, setProfiles] = useState([]);
    const [profileRoles, setProfileRoles] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingRoles, setIsLoadingRoles] = useState(false);
    const [error, setError] = useState(null);
    const [expandedProfileId, setExpandedProfileId] = useState(null);
    const { roles } = useContext(UserContext);

    async function fetchProfiles() {
        setIsLoading(true);
        try {
            // Always fetch only active profiles
            const response = await api.getJalapenoProfiles(true, accountType);
            if (response.status === false) {
                setError(`No profiles found: ${response.msg}`);
                setProfiles([]);
            } else {
                if (response.data.rows) {
                    const formattedProfiles = response.data.rows.map((row) => ({
                        ...row,
                        approversWithNames: row.approvers,
                        approvers: row.approvers.map((item) => item.id),
                    }));
                    setProfiles(formattedProfiles);
                    // Fetch roles for all profiles
                    fetchAllProfileRoles(formattedProfiles);
                } else {
                    setProfiles([]);
                }
                setError(null);
            }
        } catch (err) {
            setError('Failed to load profiles');
            setProfiles([]);
        } finally {
            setIsLoading(false);
        }
    }

    async function fetchAllProfileRoles(profilesList) {
        setIsLoadingRoles(true);
        try {
            const rolesMap = {};
            await Promise.all(
                profilesList.map(async (profile) => {
                    try {
                        const response = await api.getProfileRoles(profile.id);
                        if (response.status !== false && response.data.rows) {
                            rolesMap[profile.id] = response.data.rows;
                        } else {
                            rolesMap[profile.id] = [];
                        }
                    } catch (err) {
                        rolesMap[profile.id] = [];
                    }
                }),
            );
            setProfileRoles(rolesMap);
        } catch (err) {
            console.error('Failed to load profile roles', err);
        } finally {
            setIsLoadingRoles(false);
        }
    }

    useEffect(() => {
        fetchProfiles();
    }, [accountType]);

    const handleRowClick = (profileId) => {
        setExpandedProfileId(expandedProfileId === profileId ? null : profileId);
    };

    // Get all unique roles across all profiles for the dropdown
    const allUniqueRoles = React.useMemo(() => {
        const rolesMap = new Map();
        Object.values(profileRoles).forEach((roles) => {
            roles.forEach((role) => {
                if (!rolesMap.has(role.id)) {
                    rolesMap.set(role.id, {
                        value: role.id.toString(),
                        label: role.description,
                        description: role.longDescription,
                    });
                }
            });
        });
        return Array.from(rolesMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [profileRoles]);

    const filteredProfiles = profiles.filter((profile) => {
        // Filter by profile name
        const matchesProfileName = profile.name.toLowerCase().includes(searchTerm.toLowerCase());

        // Filter by selected roles if any are selected
        let matchesRole = true;
        if (selectedRoles.length > 0) {
            const profileRolesList = profileRoles[profile.id] || [];
            const profileRoleIds = profileRolesList.map((role) => role.id.toString());
            matchesRole = selectedRoles.some((selectedRoleId) => profileRoleIds.includes(selectedRoleId));
        }

        return matchesProfileName && matchesRole;
    });

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col space-y-4 mb-6">
                <h1 className="text-2xl font-bold">Jalapeno Profiles (Approver View)</h1>
                <div className="flex gap-4 flex-wrap">
                    <div className="w-full max-w-sm">
                        <Input
                            type="text"
                            placeholder="Search by profile name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full"
                        />
                    </div>
                    <div className="w-full max-w-md">
                        <MultiSelect
                            options={allUniqueRoles.map((role) => ({
                                value: role.value,
                                label: `${role.label}${role.description ? ` - ${role.description}` : ''}`,
                                badgeLabel: role.label,
                            }))}
                            onValueChange={setSelectedRoles}
                            defaultValue={selectedRoles}
                            placeholder="Filter by roles..."
                            variant="default"
                            maxCount={2}
                            disabled={isLoadingRoles}
                        />
                        {isLoadingRoles && (
                            <span className="text-xs text-muted-foreground mt-1 block">
                                Loading roles for filtering...
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {error && <div className="bg-red-50 text-red-500 p-4 mb-4 rounded-md">{error}</div>}

            {isLoading ? (
                <div className="text-center py-8">
                    <div className="animate-pulse">Loading profiles...</div>
                </div>
            ) : (
                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-20">ID</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Approvers</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredProfiles.map((profile) => (
                                    <React.Fragment key={profile.id}>
                                        <TableRow
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => handleRowClick(profile.id)}
                                            data-state={expandedProfileId === profile.id ? 'expanded' : 'collapsed'}
                                        >
                                            <TableCell className="font-mono">{profile.id}</TableCell>
                                            <TableCell>{profile.name}</TableCell>
                                            <TableCell>{profile.description}</TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1">
                                                    {profile.approversWithNames.map((approver) => (
                                                        <Badge key={approver.id} variant="outline">
                                                            {approver.name}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="success">Active</Badge>
                                            </TableCell>
                                        </TableRow>
                                        {expandedProfileId === profile.id && (
                                            <TableRow className="bg-muted/20">
                                                <TableCell colSpan={5} className="p-0">
                                                    <ProfileRolesTable profileId={profile.id} />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                                {filteredProfiles.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                                            {searchTerm || selectedRoles.length > 0
                                                ? 'No profiles match your search criteria'
                                                : 'No profiles found'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export { ViewOnlyJalapenoProfiles };
